import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../hooks/api";
import { API_URLS } from "../App";
import { initializeChatWidget } from "../helpers/chatWidget";
import { reportError } from "../helpers/reportError";

/**
 * Sync helper: derive admin status directly from a ProfileAndSettings payload.
 * Mirrors `enhanceProfileAndSettings(...).isAdmin()` but as a pure function so
 * it can be used both inside the provider and in tests without instantiating
 * the cache.
 */
const deriveIsAdmin = (data: ProfileAndSettings | null): boolean => {
  if (!data) return false;
  return data.profile.type === "admin" || data.profile.roles.includes("ADMIN");
};

export { deriveIsAdmin };

const PROFILE_LIFECYCLE_ERROR = "ProfileLifecycleError";

const profileLifecycleError = (message: string): Error => {
  const error = new Error(message);
  error.name = PROFILE_LIFECYCLE_ERROR;
  return error;
};

export const isProfileLifecycleError = (error: unknown): boolean =>
  error instanceof Error && error.name === PROFILE_LIFECYCLE_ERROR;

const enhanceProfileAndSettings = (
  data: ProfileAndSettings,
): ProfileAndSettings => {
  console.log("Enhancing profile");

  if (data.chat_settings) {
    initializeChatWidget(data.chat_settings);
  } else {
    console.log("No chat token found, skipping chat widget initialization");
  }

  return {
    ...data,
    isAdmin: function (): boolean {
      return (
        data.profile.type === "admin" || data.profile.roles.includes("ADMIN")
      );
    },
    isReseller: function (): boolean {
      return (
        data.profile.type === "reseller" ||
        data.profile.roles.includes("RESELLER")
      );
    },
    isCommercial: function (): boolean {
      return (
        data.profile.type === "commercial" ||
        data.profile.roles.includes("COMMERCIAL")
      );
    },
    isNormalUser: function (): boolean {
      return (
        data.profile.type === "user" ||
        (data.profile.roles.length === 1 && data.profile.roles[0] === "USER")
      );
    },
    isOperatorUser: function (): boolean {
      return this.isAdmin() || this.isReseller() || this.isCommercial();
    },
    isMainAccount: function (): boolean {
      return (
        data.profile.isMainAccount ||
        data.profile.roles.includes("MAIN_ACCOUNT")
      );
    },
    isSubAccount: function (): boolean {
      return data.profile.isSubAccount;
    },
  };
};

interface AuthContextType {
  setProfile: (profile: ProfileAndSettings | null) => void;
  isAuthenticated: () => boolean;
  /**
   * Synchronous, render-safe admin flag derived from the cached profile.
   *
   * - `false` until the first successful `/settings` fetch completes (the
   *   provider triggers it via `useEffect` on mount when authenticated).
   * - Reflects subsequent updates from `setProfile` / `saveProfile`.
   * - Safe to consume from any component without `await`.
   */
  isAdmin: boolean;
  profile: () => Promise<ProfileAndSettings>;
  /**
   * The cached profile object, or null before the first hydration. Consumers
   * can use it as a reactive signal that the authenticated profile changed
   * (e.g. right after login) without depending on unstable function identity.
   */
  profileData: ProfileAndSettings | null;
  saveProfile: (
    dataToUpdate: ProfileAndSettings,
    file?: File | null,
  ) => Promise<ProfileAndSettings>;
}

// Create the AuthContext
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};

// AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [profileCache, setProfileCache] = useState<ProfileAndSettings | null>(
    null,
  );

  // Mirrors of the cache so `profile` can read the latest value without
  // depending on it. Keeping the callback identity stable stops every consumer
  // effect with `profile` in its dependency array from re-firing.
  const profileCacheRef = useRef<ProfileAndSettings | null>(null);
  const profileRequestRef = useRef<Promise<ProfileAndSettings> | null>(null);
  const cacheTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped whenever a newer profile is written outside the GET path
  // (saveProfile/setProfile). An older in-flight GET must not overwrite it.
  const profileEpochRef = useRef(0);

  const armCacheExpiry = useCallback(() => {
    if (cacheTimeoutRef.current) clearTimeout(cacheTimeoutRef.current);
    cacheTimeoutRef.current = setTimeout(() => {
      profileCacheRef.current = null;
      setProfileCache(null);
      cacheTimeoutRef.current = null;
    }, 60000 * 5);
  }, []);

  useEffect(
    () => () => {
      if (cacheTimeoutRef.current) clearTimeout(cacheTimeoutRef.current);
    },
    [],
  );

  const profile = useCallback(async (): Promise<ProfileAndSettings> => {
    const cached = profileCacheRef.current;
    if (cached) return Promise.resolve(cached);

    const pending = profileRequestRef.current;
    if (pending) return pending;

    const epoch = profileEpochRef.current;

    const request = api
      .get(API_URLS.SETTINGS, { timeout: 15000 })
      .then((response) => {
        // A logout may have happened while /settings was in flight. Never
        // repopulate the cache for a session that no longer exists.
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        if (!token) {
          throw profileLifecycleError("Sessão terminada");
        }

        // A newer profile was saved (or set) after this request started; its
        // data wins over this stale response.
        if (epoch !== profileEpochRef.current) {
          const latest = profileCacheRef.current;
          if (latest) return latest;
          throw profileLifecycleError("Perfil substituído");
        }

        const profileData = enhanceProfileAndSettings(
          response.data as ProfileAndSettings,
        );
        profileCacheRef.current = profileData;
        setProfileCache(profileData);
        armCacheExpiry();
        return profileData;
      })
      .catch((error) => {
        // Expected lifecycle outcomes are not failures to report.
        if (!isProfileLifecycleError(error)) {
          reportError("profile.fetch", error);
        }
        throw error; // Re-throw to allow caller to catch
      });

    const wrappedRequest = request.finally(() => {
      if (profileRequestRef.current === wrappedRequest) {
        profileRequestRef.current = null;
      }
    });

    profileRequestRef.current = wrappedRequest;
    return wrappedRequest;
  }, [armCacheExpiry]);

  const saveProfile = useCallback(
    async (dataToUpdate: ProfileAndSettings, file?: File | null) => {
      const response = await api
        .put(API_URLS.SETTINGS, dataToUpdate, { timeout: 20000 })
        .then(async (res) => {
          if (file) {
            // If a file is provided, upload it as well
            const formData = new FormData();
            formData.append("file", file);
            formData.append("data", JSON.stringify(dataToUpdate));
            return await api.put(
              API_URLS.SETTINGS + "/company-logo",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
                timeout: 20000,
              },
            );
          }
          return res;
        });
      const profileData = enhanceProfileAndSettings(
        response.data as ProfileAndSettings,
      );
      profileEpochRef.current += 1;
      profileRequestRef.current = null;
      profileCacheRef.current = profileData;
      setProfileCache(profileData);
      armCacheExpiry();
      return profileData;
    },
    [armCacheExpiry],
  );

  const isAuthenticated = useCallback(() => {
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    return !!token;
  }, []);

  const setProfile = useCallback(
    (profileData: ProfileAndSettings | null) => {
      profileEpochRef.current += 1;
      profileRequestRef.current = null;

      if (!profileData) {
        if (cacheTimeoutRef.current) {
          clearTimeout(cacheTimeoutRef.current);
          cacheTimeoutRef.current = null;
        }
        profileCacheRef.current = null;
        setProfileCache(null);
        return;
      }
      const enhanced = enhanceProfileAndSettings(profileData);
      profileCacheRef.current = enhanced;
      setProfileCache(enhanced);
      armCacheExpiry();
    },
    [armCacheExpiry],
  );

  // Synchronous, render-safe admin flag. Recomputed cheaply on every render;
  // memoised so the context `value` object is stable when `profileCache` does
  // not change, avoiding spurious re-renders in consumers.
  const isAdmin = useMemo(() => deriveIsAdmin(profileCache), [profileCache]);

  // Hydrate the cache on mount when the user is already authenticated, so
  // consumers can read `isAdmin` synchronously after the first tick instead
  // of waiting for some downstream component to call `profile()`.
  //
  // We intentionally swallow errors here: `profile()` already logs failures
  // and individual screens still trigger their own fetches, so this hook is a
  // best-effort warm-up. The `hydrated` guard avoids re-fetching when callers
  // populate the cache through `setProfile` (e.g. right after login) before
  // this effect runs.
  const hydrate = useCallback(() => {
    if (profileCache) return;
    if (!isAuthenticated()) return;
    void profile().catch(() => {
      /* logged inside profile() */
    });
  }, [profileCache]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <AuthContext.Provider
      value={{
        setProfile,
        isAuthenticated,
        isAdmin,
        profile,
        profileData: profileCache,
        saveProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
